import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../api/client";

function useWindowWidth() {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return width;
}

function splitAddress(address) {
  if (!address) return { street: "", postcode: "", city: "" };
  const parts = address.split(",").map(p => p.trim());
  return {
    street:   parts[0] || "",
    postcode: parts[1] || "",
    city:     parts[2] || "",
  };
}

function joinAddress(street, postcode, city) {
  return [street, postcode, city].filter(Boolean).join(", ");
}

export default function EditCompany() {
  const { id } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 768;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState(false);

  // Locked fields (from registry)
  const [lockedName, setLockedName] = useState("");
  const [lockedCountry, setLockedCountry] = useState("");
  const [lockedCoc, setLockedCoc] = useState("");

  // Editable fields
  const [form, setForm] = useState({
    email: "",
    phone: "",
    street: "",
    postcode: "",
    city: "",
    website: "",
    btw_number: "",
  });

  // Members (read-only)
  const [members, setMembers] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [res, mem] = await Promise.all([
          api(`/companies/${id}`, { token }),
          api(`/companies/${id}/members`, { token }),
        ]);
        if (!alive) return;
        const c = res.company || res;
        setLockedName(c.name || "");
        setLockedCountry(c.country || "");
        setLockedCoc(c.chamber_of_commerce || "");
        const addr = splitAddress(c.address);
        setForm({
          email:      c.email || "",
          phone:      c.phone || "",
          street:     addr.street,
          postcode:   addr.postcode,
          city:       addr.city,
          website:    c.website || "",
          btw_number: c.btw_number || "",
        });
        setMembers(Array.isArray(mem?.members) ? mem.members : []);
      } catch (e) {
        if (alive) setErr("Failed to load company.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [id, token]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const onSave = async (e) => {
    e.preventDefault();
    setErr("");
    setSuccess(false);
    setSaving(true);
    try {
      await api(`/companies/${id}`, {
        method: "PUT",
        token,
        body: {
          name:                lockedName,
          country:             lockedCountry,
          chamber_of_commerce: lockedCoc || null,
          email:               form.email,
          phone:               form.phone || null,
          address:             joinAddress(form.street, form.postcode, form.city) || null,
          website:             form.website || null,
          btw_number:          form.btw_number || null,
        },
      });
      setSuccess(true);
      setTimeout(() => navigate(-1), 1000);
    } catch (e) {
      setErr(e.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    border: "1px solid #d0dcea", borderRadius: 6, padding: "8px 10px",
    fontSize: 14, width: "100%", boxSizing: "border-box",
  };

  const lockedStyle = {
    fontSize: 14, color: "#1e3a5f", padding: "8px 10px",
    background: "#f4f7fb", borderRadius: 6, border: "1px solid #e8eef5",
    width: "100%", boxSizing: "border-box",
  };

  const labelStyle = {
    display: "block", fontSize: 13, fontWeight: 500, color: "#3a5068", marginBottom: 4,
  };

  const lockedLabelStyle = {
    display: "block", fontSize: 13, fontWeight: 500, color: "#94afc9", marginBottom: 4,
  };

  const cardStyle = {
    background: "white", borderRadius: 12, border: "1px solid #d0dcea", padding: "24px",
  };

  if (loading) return <div style={{ paddingTop: 32, paddingLeft: 24, color: "#6b8cae", fontSize: 14 }}>Loading…</div>;

  return (
    <div style={{ maxWidth: 672, margin: "0 auto", display: "flex", flexDirection: "column", gap: 24 }}>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <h1 style={{ fontSize: 22, fontWeight: 600, color: "#1e3a5f", margin: 0 }}>Edit Company</h1>
        <button
          type="button"
          onClick={() => navigate(-1)}
          style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "#6b8cae", padding: 0 }}
        >
          ← Back
        </button>
      </div>

      {err && (
        <div style={{ fontSize: 14, color: "#c0392b", border: "1px solid #fdecea", borderRadius: 8, padding: 12, background: "#fdecea" }}>
          {err}
        </div>
      )}
      {success && (
        <div style={{ fontSize: 14, color: "#2e7d32", border: "1px solid #e6f4ea", borderRadius: 8, padding: 12, background: "#e6f4ea" }}>
          ✓ Saved
        </div>
      )}

      <form onSubmit={onSave} style={{ ...cardStyle, display: "flex", flexDirection: "column", gap: 16 }}>

        {/* Locked fields */}
        <div style={{ display: "flex", flexDirection: "column", gap: 4, paddingBottom: 4 }}>
          <div style={{ fontSize: 12, color: "#94afc9", fontWeight: 500, letterSpacing: "0.04em", textTransform: "uppercase" }}>
            Verified by registry — cannot be edited
          </div>
        </div>

        <div>
          <label style={lockedLabelStyle}>Company name</label>
          <div style={lockedStyle}>{lockedName || "—"}</div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 16 }}>
          <div>
            <label style={lockedLabelStyle}>Country</label>
            <div style={lockedStyle}>{lockedCountry || "—"}</div>
          </div>
          <div>
            <label style={lockedLabelStyle}>Chamber of Commerce</label>
            <div style={lockedStyle}>{lockedCoc || "—"}</div>
          </div>
        </div>

        <div style={{ borderTop: "1px solid #e8eef5", paddingTop: 16, display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{ fontSize: 12, color: "#3a5068", fontWeight: 500, letterSpacing: "0.04em", textTransform: "uppercase" }}>
            Editable details
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 16 }}>
          <div>
            <label style={labelStyle}>Email</label>
            <input type="email" name="email" value={form.email} onChange={onChange} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Phone</label>
            <input name="phone" value={form.phone} onChange={onChange} style={inputStyle} />
          </div>
        </div>

        <div>
          <label style={labelStyle}>Street address</label>
          <input name="street" value={form.street} onChange={onChange} placeholder="e.g. Radiumstraat 12" style={inputStyle} />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 2fr", gap: 16 }}>
          <div>
            <label style={labelStyle}>Postal code</label>
            <input name="postcode" value={form.postcode} onChange={onChange} placeholder="e.g. 9714 CZ" style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>City</label>
            <input name="city" value={form.city} onChange={onChange} placeholder="e.g. Groningen" style={inputStyle} />
          </div>
        </div>

        <div>
          <label style={labelStyle}>Website</label>
          <input name="website" value={form.website} onChange={onChange} placeholder="https://..." style={inputStyle} />
        </div>

        <div>
          <label style={labelStyle}>BTW number</label>
          <input name="btw_number" value={form.btw_number} onChange={onChange} placeholder="e.g. NL123456789B01" style={inputStyle} />
        </div>

        <div style={{ paddingTop: 8 }}>
          <button
            type="submit"
            disabled={saving}
            style={{
              width: "100%", padding: "10px 16px", background: "#1e3a5f", color: "white",
              border: "none", borderRadius: 8, fontSize: 14,
              cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.4 : 1,
            }}
          >
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </form>

      {/* Members — read only */}
      <div style={cardStyle}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#1e3a5f", marginBottom: 12 }}>Company members</div>
        {members.length === 0 ? (
          <div style={{ fontSize: 14, color: "#6b8cae" }}>No members linked to this company.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {members.map((m) => (
              <div key={m.id} style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                border: "1px solid #d0dcea", borderRadius: 8, padding: "8px 12px", fontSize: 14,
              }}>
                <div>
                  <span style={{ fontWeight: 500, color: "#1e3a5f" }}>{m.email}</span>
                  <span style={{ marginLeft: 8, color: "#6b8cae", fontSize: 12 }}>{m.role_in_company}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
}